import { FCC } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Box, CircularProgress, Modal, Stack, styled, Typography } from "@mui/material";
import { background, black, divider, text } from "@/theme/colors";
import { IconBox } from "@/components/elements/IconBox";
import { IconClose, IconCompanyListImport } from "@/assets/icons";
import { rounded } from "@/theme/variables";
import { DropZone } from "@/components/elements/DropZone";
import { Button } from "@/components/elements/Button";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useMemo, useState } from "react";
import { TExtendFile } from "@/types/file";
import { downloadSample, validateFileFormat } from "@/features/talent/utils/excel";
import { FILE_INCORRECT } from "@/features/company/constants/excel";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { validateImportTalentApi } from "@/api/services/main/talent";
import { TTalentRowExcel } from "@/features/talent/types/talentExcel";
import { AppRoutes } from "@/routes/config";
import { useGetCompanySpecificIndustryList, useGetResidenceStatusList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { useGetMatchInfo } from "@/api/services/main/match";
import { useGetUserListPersonInCharge } from "@/api/services/main/user";
import { sanitizeInput } from "@/utils/pieces";
import Papa from "papaparse";
import chardet from "chardet";
import iconv from "iconv-lite";
import { MAX_LENGTH_ROWS, MAX_SIZE } from "@/features/talent/constants/file";
import { TTalent } from "@/api/entities/talent.entity";
import { FileRejection } from "react-dropzone";
import { getExtensionsOfMimeTypes, TFileRules } from "@/utils/file";
import { toast } from "@/utils/toast";

type TImportPersonnelModalProps = {
  modal: UseVisibleReturn;
  isLoading?: boolean;
};

type TErrorItem = {
  message: string;
};

type FileType = "xlsx" | "csv";

export const ImportTalentModal: FCC<TImportPersonnelModalProps> = ({ modal, isLoading }) => {
  const navigate = useNavigate();
  const [isSubmit, setIsSubmit] = useState(false);
  const [file, setFile] = useState<File | null>();
  const [errorList, setErrorList] = useState<string[]>([]);

  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { residenceStatusOptions } = useGetResidenceStatusList();
  const { data: recruitmentStatusOptions } = useEnum("ERecruitmentStatus");
  const { data: educationLevelOptions } = useEnum("EEducationLevel");

  // Master data for 紹介事業所名 and 人材担当者 dropdowns in template
  const { data: matchInfoResponse } = useGetMatchInfo();
  const matchOfficeOptions = useMemo(() => {
    const matchOffices = matchInfoResponse?.data?.matchOffices;
    if (!matchOffices || !Array.isArray(matchOffices)) return [];
    return matchOffices
      .filter((o) => o?.id && o?.officeName?.trim())
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
      .map((o) => ({ label: o.officeName!, value: o.id! }));
  }, [matchInfoResponse]);

  const { userOptions: personInChargeOptions } = useGetUserListPersonInCharge();

  const rules: TFileRules = {
    acceptFileTypesExtension: [".csv", ".xlsm", ".xlsx"],
    acceptMimeTypes: [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel.sheet.macroEnabled.12",
    ],
  };

  const handleErrorImportCsv = (errorContainer: unknown) => {
    if (typeof errorContainer === "object" && errorContainer !== null) {
      if ("error" in errorContainer) {
        const listErrors = (errorContainer as { error: unknown }).error;
        if (Array.isArray(listErrors)) {
          setErrorList(listErrors.map((item: TErrorItem) => item.message));
        }
      } else if ("message" in errorContainer) {
        setErrorList([(errorContainer as { message: unknown }).message as string]);
      }
    } else {
      setErrorList(["Unknown error"]);
    }
  };

  const onClose = () => {
    setIsSubmit(false);
    setFile(null);
    setErrorList([]);
    modal.close();
  };
  const handleChangeFiles = async (value: TExtendFile | TExtendFile[] | null) => {
    const files = [value ?? []].flat().filter(Boolean);
    if (!files[0]) {
      setFile(null);
      return;
    }
    const data = files[0];
    setFile(data);
    setErrorList([]);
  };

  const handleFileRejections = (fileRejections: FileRejection[]) => {
    const listFileErrorSize: string[] = [];
    const listFileErrorType: string[] = [];

    fileRejections.forEach((fileReject) => {
      const file = fileReject.file;
      if (file.size > MAX_SIZE) {
        listFileErrorSize.push(file.name);
      }
      const ext = file.name.split(".").pop()?.toLowerCase();
      const isValidMimeType = rules.acceptMimeTypes?.includes(file.type) ?? false;
      const isValidExtension = rules.acceptFileTypesExtension?.includes(`.${ext}`) ?? false;

      if (!isValidMimeType && !isValidExtension) {
        listFileErrorType.push(file.name);
      }
    });

    if (listFileErrorType.length > 0) {
      const extensions = getExtensionsOfMimeTypes([".csv", ".xlsm", ".xlsx"]);

      const errorType =
        `以下のファイル形式はアップロードできません。\n` +
        `${listFileErrorType.map((fileName) => `◦${fileName}`).join("\n")}\n` +
        `（アップロード可能なファイル形式は${extensions.join("、")}です。）`;

      toast.error(errorType);
      return;
    }

    if (listFileErrorSize.length > 0) {
      const errorSize = `以下のファイルはサイズ上限（${MAX_SIZE / 1000}KB）を超えているため、アップロードできませんでした：
          ${listFileErrorSize.map((fileName) => `・${fileName}`).join("\n")}
          `;

      toast.error(errorSize);
      return;
    }
  };

  const getFileType = (file: File): FileType => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension === "csv") return "csv";
    if (extension === "xlsx" || extension === "xlsm") return "xlsx";
    throw new Error(FILE_INCORRECT);
  };

  const readCSVFile = async (file: File): Promise<string[][]> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      const buffer = Buffer.from(uint8Array);
      if (!buffer) {
        throw new Error(FILE_INCORRECT);
      }
      const fileEncode = chardet.detect(buffer);
      if (!fileEncode) {
        throw new Error(FILE_INCORRECT);
      }
      const fileContent = iconv.decode(buffer, fileEncode);
      return new Promise((resolve, reject) => {
        Papa.parse(fileContent, {
          complete: (results) => {
            const rows = results.data as string[][];
            const { isUpdate } = validateFileFormat(rows[0]);
            if (isUpdate) {
              rows.forEach((row: string[]) => {
                if (row[0]) {
                  row.unshift("is_update");
                }
              });
            }
            resolve(rows);
          },
          error: (error: never) => {
            reject(error);
          },
          skipEmptyLines: false,
          dynamicTyping: false, // Keep as strings
        });
      });
    } catch (e) {
      throw new Error(FILE_INCORRECT);
    }
  };

  const readXLSXFile = async (file: File): Promise<string[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, {
            type: "array",
          });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows: [][] = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            raw: false,
            range: "A1:AY209",
          });
          const { isUpdate } = validateFileFormat(rows[0]);
          if (isUpdate) {
            rows.forEach((row: string[]) => {
              if (row[0]) {
                row.unshift("is_update");
              }
            });
          }
          resolve(rows);
        } catch (err) {
          reject(err);
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const readFileImport = async (file: File): Promise<string[][]> => {
    try {
      const fileType = getFileType(file);

      if (fileType === "csv") {
        return await readCSVFile(file);
      } else {
        return await readXLSXFile(file);
      }
    } catch (e) {
      throw new Error(FILE_INCORRECT);
    }
  };

  const handleImportFile = async () => {
    if (!file) return;
    try {
      setIsSubmit(true);

      const rows: string[][] = await readFileImport(file);
      if (rows.length > MAX_LENGTH_ROWS) {
        throw new Error(FILE_INCORRECT);
      }
      const totalRows = rows.length;
      const results = [];

      // Build key array dynamically from actual header names
      const headerToKey: Record<string, string> = {
        "求職活動ステータス": "recruitmentStatus",
        "紹介事業所名": "matchOfficeName",
        "人材担当者": "personalInCharge",
        "氏名": "fullName",
        "カナ表記": "fullNameKana",
        "生年月日": "birthday",
        "呼び名": "nickName",
        "人材管理番号": "talentNumber",
        "国籍": "nationality",
        "性別": "gender",
        "電話番号": "phoneNumber",
        "メールアドレス": "email",
        "その他の連絡手段・SNS①": "contactType",
        "その他の連絡先ID等①": "contactInfo",
        "その他の連絡手段・SNS②": "contactType2",
        "その他の連絡先ID等②": "contactInfo2",
        "その他の連絡手段・SNS③": "contactType3",
        "その他の連絡先ID等③": "contactInfo3",
        "その他の連絡手段・SNS④": "contactType4",
        "その他の連絡先ID等④": "contactInfo4",
        "居住地": "location",
        "郵便番号": "zipCode",
        "所在地（町名丁目番地号等）": "address",
        "本国における居住地": "country",
        "在留資格": "residenceStatus",
        "在留期限": "periodOfStay",
        "特定技能1号起算日": "specifiedSkillFirstStartDate",
        "ブランク期間（月単位）": "specifiedSkillFirstRemainingTerm",
        "パスポート番号": "passportNumber",
        "パスポート期限": "passportExpiryDate",
        "分野": "specificBusinessClassificationFirst",
        "区分": "specificBusinessClassificationTypeFirst",
        "日本語能力証明書等（実習修了証明含む）": "languageCertificates",
        "日本語能力（その他）": "languageTestOther",
        "特定技能合格証": "specificSkillCertificates",
        "職種": "majorName",
        "作業": "jobName",
        "特性メモ": "memo",
        "面談評価": "interviewEvaluation",
        "家族構成": "familyInformation",
        "希望職種（分野）": "desiredJobType",
        "希望勤務地": "desiredWorkLocation",
        "希望手取り額": "expectSalary",
        "転職可能時期": "availableFrom",
        "その他希望": "otherRequest",
        "社内メモ": "internalMemo",
        "面談日": "interviewDate",
        "資格": "heldQualifications",
        "学歴": "educationBackground",
        "パートナー名": "partnerName",
      };

      const headerRow = rows[0].map((h) => String(h).replace(/^\uFEFF/, "").trim());
      const keys: string[] = [];
      for (let i = 0; i < headerRow.length; i++) {
        const h = headerRow[i];
        if (h === "") {
          keys.push(`_skip_${i}`);
        } else {
          keys.push(headerToKey[h] || `_unknown_${i}`);
        }
      }
      keys.push("imageUrl");
      for (let i = 1; i < totalRows; i++) {
        const row: string[] = rows[i];
        if (row.length === 0) continue;

        if (row.every((cell) => String(cell).trim() === "")) {
          continue;
        }

        results.push(row);
      }
      let formattedResults: TTalentRowExcel[];
      if (rows[0].some((row) => row === "is_update")) {
        formattedResults = results.map((row) => {
          if (row[0] === "is_update") {
            return {
              recruitmentStatus: "is_update",
              fullName: row[1],
              birthday: row[2],
              talentNumber: row[3],
              imageUrl: row[4],
            };
          } else {
            return {
              recruitmentStatus: "is_update",
              fullName: row[0],
              birthday: row[1],
              talentNumber: row[2],
              imageUrl: row[3],
            };
          }
        });
      } else {
        formattedResults = results.map((row) => {
          return Object.fromEntries(
            keys.map((key, index) => {
              if (key === "imageUrl") {
                try {
                  const str = row[index];
                  const cleaned = typeof str === "string" ? str.replace(/[\u0000-\u001F\u007F-\u009F]/g, "") : str;
                  return [key, cleaned];
                } catch (e) {
                  console.error("Failed parse imageUrl JSON:", row[index], e);
                  return [key, null];
                }
              }
              const value = row[index];
              const trimmed = typeof value === "string" ? sanitizeInput(value) : (value ?? "");
              return [key, trimmed];
            }),
          ) as TTalentRowExcel;
        });
      }
      const { data: talentResponse } = await validateImportTalentApi(formattedResults);
      // Keep rows that have at least fullName or nickName (draft rows may have only nickName)
      const talents = talentResponse.filter((talent: TTalent) => talent?.fullName != null || talent?.nickName != null);
      modal.close();
      navigate(AppRoutes.talentImport, { state: { talents } });
    } catch (err) {
      setIsSubmit(false);
      handleErrorImportCsv(err);
    }
  };
  return (
    <Modal
      open={modal.visible}
      hideBackdrop={false}
      sx={{
        position: "fixed",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ModalContainer width={920}>
        <Stack
          bgcolor={background.disable}
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          height="53px"
          sx={{
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            padding: "16px",
          }}
        >
          <Typography variant="body14Light" color={text.primary}>
            人材情報 | 一括インポート
          </Typography>
          <IconBox onClick={onClose} sx={{ p: 0 }}>
            <IconClose color={black[80]} fontSize={24} />
          </IconBox>
        </Stack>
        <Stack
          sx={{
            padding: "32px 16px ",
            gap: "24px",
          }}
        >
          {!isSubmit && (
            <Box borderColor={divider.middle} borderRadius={rounded.sm}>
              <DropZone
                options={{ multiple: false }}
                onChange={handleChangeFiles}
                handleFileRejections={handleFileRejections}
                rules={{
                  maxFile: 1,
                  maxSize: 10 * 1000 * 1000,
                  acceptMimeTypes: rules.acceptMimeTypes,
                }}
                isShowErr={false}
              >
                <Typography variant="body14Light" whiteSpace="pre-wrap" textAlign="center">
                  アップロードするファイルをドラッグ&ドロップ
                </Typography>
                <Typography variant="body14Light" color={black[90]} whiteSpace="pre-wrap" textAlign="center">
                  （ファイル形式：csv、xlsx、xlsmに対応）
                </Typography>
                <Typography variant="body14Light" width={920} textAlign="center">
                  または
                </Typography>
                <Button type="button" variant="outline">
                  ファイルを選択する
                </Button>
                <Typography variant="cap12Light" color={black[70]} textAlign="start">
                  <ul>
                    <li>データ内に改行は含めないでください。</li>
                    <li>容量は最大10MBまでとなります。</li>
                  </ul>
                </Typography>
              </DropZone>
              {errorList.length > 0 && (
                <Stack
                  sx={{ mt: "32px", p: "8px", background: background.error, color: text.error, maxHeight: "160px", overflowY: "auto" }}
                  borderRadius={rounded.xs}
                >
                  {errorList.map((item, index) => (
                    <Typography key={index} component="p" variant="cap12">
                      {item}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          )}
          {isSubmit && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", height: "150px" }}>
              <Box sx={{ height: 60 }}>
                <CircularProgress />
              </Box>
              <Typography variant="body14Light" textAlign="center">
                処理中
              </Typography>
            </Box>
          )}
        </Stack>
        <hr />
        <Stack
          flexDirection="row"
          gap="16px"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            padding: "16px 32px",
          }}
        >
          <Stack flexDirection="row" gap="16px" justifyContent="flex-start">
            <Button variant="ghost" onClick={onClose}>
              キャンセル
            </Button>
          </Stack>
          <Stack flexDirection="row" gap="16px" justifyContent="flex-end">
            {!isSubmit && (
              <Button
                onClick={() =>
                  downloadSample({
                    industry: companySpecificIndustryOptions,
                    residenceStatus: residenceStatusOptions,
                    recruitmentStatus: recruitmentStatusOptions,
                    matchOffice: matchOfficeOptions,
                    personInCharge: personInChargeOptions,
                    educationLevel: educationLevelOptions,
                  })
                }
                variant="outline"
                startIcon={<FileDownloadIcon />}
              >
                テンプレートをダウンロード
              </Button>
            )}
            <Button
              onClick={handleImportFile}
              isLoading={isLoading}
              disabled={isSubmit || !file || errorList.length > 0}
              startIcon={<IconCompanyListImport />}
            >
              {isSubmit ? "ファイルを処理中..." : "インポート"}
            </Button>
          </Stack>
        </Stack>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  min-width: 417px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  outline: none; /* Remove default focus outline */

  &:focus {
    outline: none;
  }
`;
